import { useCallback } from "react";
import { useEvergisContext, getCrs } from "@evergis/react";
import { Bbox } from "@evergis/sgis";

import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { useDispatch } from "react-redux";
import { ProjectInfo } from "../../interfaces/resourceInfo";
import { loadImage } from "../../utils/image";
import { addNotification } from "../../ducks/notifications/notifications";
import { ResourceMeta } from "../useResourceMeta";

interface UseGeneratePreviewParams {
  project: ProjectInfo;
  preview?: ResourceMeta["preview"];
  onPreviewGenerated?: (dataUrl: string, file: File, isLoading?: boolean) => void;
  onError?: (error: Error) => void;
}

interface UseGeneratePreviewData {
  dataUrl?: string;
  file?: File;
}

export function useGeneratePreview({ project, preview, onPreviewGenerated, onError }: UseGeneratePreviewParams) {
  const api = useEvergisContext();

  return useCallback(async (): Promise<UseGeneratePreviewData> => {
    try {
      const {
        content: { items, resolution, position, srid, baseMapName },
      } = project;

      onPreviewGenerated && onPreviewGenerated(preview.src, preview.file, true);
      const bbox = getBbox(fixProjectPosition(position), resolution, srid);
      const compress = 0.2;

      const imgWidth = Math.round((bbox.xMax - bbox.xMin) / resolution) * compress;
      const imgHeight = Math.round((bbox.yMax - bbox.yMin) / resolution) * compress;

      const projectsLayers = items?.slice().reverse() ?? [];

      const layers = [baseMapName].concat(projectsLayers.filter(({ isVisible }) => isVisible).map(({ name }) => name));

      const urls = layers.map(name =>
        api.layers.getLayerImage({
          name,
          extent: bbox.coordinates,
          size: [imgWidth, imgHeight],
          extentSr: bbox.crs.wkid,
        }),
      );

      const responses: Array<HTMLImageElement | Error> = await Promise.all(
        urls.map(url =>
          loadImage(url)
            .then(img => img)
            .catch(error => error),
        ),
      );

      if (responses.every((res): res is Error => res instanceof Error)) {
        onPreviewGenerated && onPreviewGenerated(preview.src, preview.file, false);

        throw Error("Не удалось загрузить ни одного слоя");
      }

      const images = responses.filter((res): res is HTMLImageElement => res instanceof HTMLImageElement);
      const [dataUrl, file] = await composeImages(images);

      onPreviewGenerated && onPreviewGenerated(dataUrl, file, false);

      return { dataUrl, file };
    } catch (error) {
      onPreviewGenerated && onPreviewGenerated(preview.src, preview.file, false);

      onError(error);

      return {};
    }
  }, [project, preview, api, onPreviewGenerated, onError]);
}

export const getBbox = ([x, y]: Coordinates, resolution: number, srid: number): Bbox => {
  const offsetX = resolution * (window.screen.width / 2);
  const offsetY = resolution * (window.screen.height / 2);

  return new Bbox([x - offsetX, y - offsetY], [x + offsetX, y + offsetY], getCrs(srid));
};

const composeImages = (imgs: HTMLImageElement[]): Promise<[string, File]> => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  const maxWidth = Math.max(...imgs.map(({ naturalWidth }) => naturalWidth));
  const maxHeight = Math.max(...imgs.map(({ naturalHeight }) => naturalHeight));

  canvas.width = maxWidth;
  canvas.height = maxHeight;

  imgs.forEach(img => {
    context.drawImage(img, 0, 0, maxWidth, maxHeight);
  });

  const dataUrl = canvas.toDataURL("image/png");

  return new Promise(res => {
    canvas.toBlob(blob => res([dataUrl, new File([blob], "preview")]));
  });
};

// TODO fix ProjectInfo types
export const fixProjectPosition = (position: ProjectInfo["content"]["position"] | Coordinates): Coordinates => {
  if (Array.isArray(position)) {
    return position as Coordinates;
  }

  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return [position.x, position.y];
};

export function useGeneratePreviewErrorNotification() {
  const dispatch = useDispatch();

  return useCallback((error: Error) => {
    dispatch(addNotification({ title: "Не удалось сгененерировать превью", error: true, description: error.message }));
  }, []);
}
